import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Camera, FilePdf } from "@phosphor-icons/react";
import { ReportSummaryChip } from "@/components/spb/SpbReportComponents";

const todayWib = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
const num = (v) => (v == null ? "-" : Number(v).toLocaleString("id-ID", { maximumFractionDigits: 3 }));

export default function DailyProofReport() {
  const [tanggal, setTanggal] = useState(todayWib());
  const [data, setData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const token = useRef(0);

  const load = useCallback(async () => {
    if (!tanggal) return;
    const my = ++token.current;
    setLoading(true);
    try {
      const { data: d } = await api.get("/reports/daily-proofs", { params: { tanggal } });
      if (my === token.current) setData(d);
    } catch {
      if (my === token.current) setData({ items: [], summary: {} });
    } finally {
      if (my === token.current) setLoading(false);
    }
  }, [tanggal]);

  useEffect(() => { load(); }, [load]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_BASE}/reports/daily-proofs/export.pdf?tanggal=${tanggal}`, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `bukti_muat_harian_${tanggal}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("PDF bukti muat berhasil diunduh");
    } catch (e) { toast.error(`Export gagal: ${e.message}`); }
    finally { setDownloading(false); }
  };

  const s = data.summary || {};
  return (
    <div className="max-w-[1400px] mx-auto p-4 lg:p-8 space-y-6" data-testid="daily-proof-page">
      <div className="border-b border-border pb-4 flex items-center gap-3">
        <Camera size={28} weight="duotone" className="text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Laporan Harian Bukti Muat</h1>
          <p className="text-sm text-muted-foreground mt-1">Semua SPB dengan foto bukti muat yang diunggah pada tanggal terpilih (WIB), digabung jadi satu PDF.</p>
        </div>
      </div>

      <div className="border border-border bg-card p-4 flex flex-wrap items-end gap-4" data-testid="daily-proof-filter-bar">
        <div>
          <label className="overline block mb-1 text-muted-foreground">Tanggal</label>
          <input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)}
            className="border border-border bg-background px-3 py-2 text-sm rounded-md" data-testid="daily-proof-date" />
        </div>
        <div className="flex-1" />
        <Button onClick={downloadPdf} disabled={downloading || !s.spb_count} className="rounded-md gap-2" data-testid="daily-proof-pdf-btn">
          <FilePdf size={18} weight="duotone" /> {downloading ? "Menyiapkan PDF…" : "Unduh PDF"}
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="daily-proof-summary">
        <ReportSummaryChip label="Jumlah SPB" value={s.spb_count ?? 0} />
        <ReportSummaryChip label="Jumlah Foto" value={s.photo_count ?? 0} />
      </div>
      {s.truncated && (
        <div className="border border-amber-400 bg-amber-50 text-amber-800 text-sm p-3" data-testid="daily-proof-truncated">
          Lebih dari {s.max_photos} foto pada tanggal ini — hanya {s.max_photos} foto pertama yang ditampilkan dan dimasukkan ke PDF.
        </div>
      )}

      {loading && <div className="text-sm text-muted-foreground" data-testid="daily-proof-loading">Memuat…</div>}
      {!loading && <ProofTable items={data.items} />}
    </div>
  );
}

function ProofTable({ items }) {
  return (
    <div className="border border-border overflow-x-auto" data-testid="daily-proof-table">
      <table className="w-full text-sm">
        <thead className="bg-muted">
          <tr className="text-left">
            {["No SPB", "No Polisi", "Sopir", "Distrik", "Termuat", "Petugas", "Jam", "Foto"].map((h) => (
              <th key={h} className="px-3 py-2 font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground" data-testid="daily-proof-empty">Tidak ada bukti muat pada tanggal ini.</td></tr>
          )}
          {items.map((it, i) => (
            <tr key={it.spb_id} className="border-t border-border hover:bg-muted/40" data-testid={`daily-proof-row-${i}`}>
              <td className="px-3 py-2 font-mono font-medium">{it.no_spb}</td>
              <td className="px-3 py-2">{it.no_polisi}</td>
              <td className="px-3 py-2">{it.nama_supir}</td>
              <td className="px-3 py-2">{it.distrik_nama}</td>
              <td className="px-3 py-2 font-mono">{num(it.jumlah_termuat)} {it.satuan}</td>
              <td className="px-3 py-2">{it.petugas}</td>
              <td className="px-3 py-2 font-mono">{it.jam_upload}</td>
              <td className="px-3 py-2 font-mono">{it.photo_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
